import { Controller } from "@hotwired/stimulus"
import VoiceRecorder from "../utils/voice_recorder"

// The mic on a person-to-person composer (shared/_message_composer): the
// same push-to-talk as Jane's composer mic — tap to record, tap again to stop
// — through the same recorder (utils/voice_recorder). The one difference is
// what happens to the words: Jane's composer sends them, but this message is
// going to a person, so the transcript lands in the field for you to check
// and send yourself. Esc while recording throws the recording away.
export default class extends Controller {
    static targets = ["field", "button", "icon"]

    connect() {
        this._recorder = new VoiceRecorder({
            onState: (state) => this._render(state),
            onText: (text) => this._insert(text),
            onError: (message) => this._flash(message)
        })
        this._placeholder = this.fieldTarget.placeholder
        this._render("idle")
    }

    disconnect() {
        this._recorder.cancel()
        if (this._flashTimer) clearTimeout(this._flashTimer)
    }

    toggle(event) {
        event?.preventDefault()
        if (this._recorder.state === "recording") this._recorder.stop()
        else if (this._recorder.state === "idle") this._recorder.start()
    }

    cancel(event) {
        if (event.key !== "Escape" || this._recorder.state !== "recording") return
        event.preventDefault()
        this._recorder.cancel()
    }

    // Appended, not replaced: a half-typed message keeps what is already there.
    // The input event lets the composer's other controllers (local draft,
    // typing heartbeat) see the new text as if it had been typed.
    _insert(text) {
        const field = this.fieldTarget
        const before = field.value.replace(/\s+$/, "")
        field.value = before ? `${before} ${text}` : text
        field.dispatchEvent(new Event("input", { bubbles: true }))
        field.focus()
        field.setSelectionRange?.(field.value.length, field.value.length)
    }

    _render(state) {
        const btn = this.buttonTarget
        const icon = this.iconTarget
        btn.classList.toggle("is-recording", state === "recording")
        btn.classList.toggle("is-busy", state === "transcribing")
        this.fieldTarget.readOnly = state !== "idle"
        if (state === "recording") {
            btn.setAttribute("aria-label", "Stop dictating")
            btn.title = "Stop dictating"
            icon.className = "bi bi-stop-fill"
            this.fieldTarget.placeholder = "Listening… tap to stop"
        } else if (state === "transcribing") {
            btn.setAttribute("aria-label", "Transcribing")
            btn.title = "Transcribing"
            icon.className = "bi bi-arrow-repeat assistant-spin"
            this.fieldTarget.placeholder = "Transcribing…"
        } else {
            btn.setAttribute("aria-label", "Dictate")
            btn.title = "Dictate — speak, then check and send"
            icon.className = "bi bi-mic"
            this.fieldTarget.placeholder = this._placeholder
        }
    }

    _flash(message) {
        this.fieldTarget.placeholder = message
        if (this._flashTimer) clearTimeout(this._flashTimer)
        this._flashTimer = setTimeout(() => {
            if (this._recorder.state === "idle") this.fieldTarget.placeholder = this._placeholder
        }, 1800)
    }
}
